import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, FolderKanban, FolderPlus, Loader2 } from 'lucide-react';
import { projectService } from '../../services/projectService';
import { chatService } from '../../services/chatService';
import { Project, STAGE_NAMES } from '../../types/project';
import { NewProjectModal, SubjectOption } from './NewProjectModal';
import { baseSubjectNameFromId, buildSubjectOptionDisplayMap } from '../../utils/subjectDisplay';

export const PROJECTS_HUB_ID = '__projects__';

type ProjectsHubProps = {
  onSelectProject: (projectId: string) => void;
  onExitToChat?: () => void;
};

export const ProjectsHub: React.FC<ProjectsHubProps> = ({ onSelectProject, onExitToChat }) => {
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscribedSubjects, setSubscribedSubjects] = useState<SubjectOption[]>([]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await projectService.list();
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load subscribed subjects for the new-project modal
  const loadSubscribedSubjects = useCallback(async () => {
    try {
      const subjectsData = await chatService.getUserSubjects();
      const rawOptions: SubjectOption[] = subjectsData
        .filter((s) => s.subject_id && s.name && s.subject_id !== 'general')
        .map((s) => ({ subject_id: s.subject_id, name: s.name }));
      const displayMap = buildSubjectOptionDisplayMap(
        rawOptions.map((option) => ({ id: option.subject_id, name: option.name }))
      );
      const options = rawOptions.map((option) => ({
        ...option,
        name: displayMap[option.subject_id] || option.name,
      }));
      setSubscribedSubjects(options);
    } catch (err) {
      console.error('Failed to load subscribed subjects:', err);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadSubscribedSubjects();
  }, [loadProjects, loadSubscribedSubjects]);

  const handleCreateProject = async (input: { title?: string; subject_id: string; goal?: string }) => {
    try {
      const created = await projectService.create({
        student_id: '', // Will be filled from auth
        subject_id: input.subject_id,
        title: input.title,  // Optional - AI can help pick a title
        project_brief: input.goal,
      });
      
      setIsNewOpen(false);
      await loadProjects();
      onSelectProject(created.id);
    } catch (err) {
      console.error('Failed to create project:', err);
      setError('Failed to create project. Please try again.');
    }
  };

  const getStageDisplay = (project: Project) => {
    const stageName = STAGE_NAMES[project.current_stage] || `Stage ${project.current_stage}`;
    return `Stage ${project.current_stage}: ${stageName}`;
  };

  const getSubjectDisplay = (project: Project) => {
    if (!project.subject_id || project.subject_id === 'general') return '—';
    return baseSubjectNameFromId(project.subject_id);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center">
            <FolderKanban className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Projects</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Create a project workspace for coaching, planning, and step-by-step completion.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onExitToChat && (
            <button
              type="button"
              onClick={onExitToChat}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Chat
            </button>
          )}
          <button
            onClick={() => setIsNewOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
          >
            <FolderPlus className="w-4 h-4 text-[var(--accent-primary)]" />
            New project
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
          <button
            onClick={loadProjects}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--accent-primary)]" />
        </div>
      ) : projects.length === 0 ? (
        <div className="p-6 rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
          <p className="text-sm text-[var(--text-secondary)]">
            No projects yet. Create one to get structured help for ZIMSEC project-based learning (SBP).
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelectProject(p.id)}
              className="text-left p-4 rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <div className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {p.title || 'Untitled Project'}
              </div>
              <div className="mt-1 text-xs text-[var(--text-secondary)] truncate">
                {getSubjectDisplay(p)}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/20">
                  {getStageDisplay(p)}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                  p.status === 'COMPLETED' 
                    ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                    : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/20'
                }`}>
                  {p.status || 'ACTIVE'}
                </span>
              </div>
              <div className="mt-2 text-[10px] text-[var(--text-tertiary)]">
                Updated {new Date(p.updated_at || p.last_activity_at || Date.now()).toLocaleDateString()}
              </div>
            </button>
          ))}
        </div>
      )}

      <NewProjectModal
        isOpen={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onCreate={handleCreateProject}
        subscribedSubjects={subscribedSubjects}
      />
    </div>
  );
};
